import React, { useState, useCallback } from 'react';
import { UserProfile } from '../../../types/types';
import { AppSyncUser } from '../../../services/auth/types/user';
import DashboardLayout from '../../layouts/DashboardLayout';
import ShareComposer from '../../components/feed/ShareComposer';
import FeedList from '../../components/feed/FeedList';
import { mockFeedPosts } from '../../data/mockFeedData';
import { FeedPost } from '../../types/feed';
import './FeedPage.css';

interface FeedPageProps {
  user: UserProfile;
  appSyncUser: AppSyncUser | null;
}

const recommendedTrips = [
  {
    title: 'Bali Adventure',
    meta: '7 days - $1,240',
    imageUrl: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?auto=format&fit=crop&w=400&q=80',
  },
  {
    title: 'Tokyo Lights',
    meta: '5 days - $980',
    imageUrl: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?auto=format&fit=crop&w=400&q=80',
  },
  {
    title: 'Paris Discovery',
    meta: '4 days - $860',
    imageUrl: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=400&q=80',
  },
  {
    title: 'Coastal Portugal',
    meta: '5 days - $1,120',
    imageUrl: 'https://images.unsplash.com/photo-1500375592092-40eb2168fd21?auto=format&fit=crop&w=400&q=80',
  },
  {
    title: 'Iceland Ring Road',
    meta: '9 days - $2,050',
    imageUrl: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=400&q=80',
  },
  {
    title: 'Morocco Colors',
    meta: '6 days - $930',
    imageUrl: 'https://images.unsplash.com/photo-1539020140153-e8c237112e53?auto=format&fit=crop&w=400&q=80',
  },
  {
    title: 'Alps Weekend',
    meta: '3 days - $640',
    imageUrl: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=400&q=80',
  },
  {
    title: 'Greek Islands',
    meta: '8 days - $1,780',
    imageUrl: 'https://images.unsplash.com/photo-1516483638261-f4dbaf036963?auto=format&fit=crop&w=400&q=80',
  },
  {
    title: 'Costa Rica Wild',
    meta: '7 days - $1,360',
    imageUrl: 'https://images.unsplash.com/photo-1518182170546-07661fd94144?auto=format&fit=crop&w=400&q=80',
  },
  {
    title: 'New York Food',
    meta: '4 days - $1,050',
    imageUrl: 'https://images.unsplash.com/photo-1485871981521-5b1fd3805eee?auto=format&fit=crop&w=400&q=80',
  },
];

const FeedPage: React.FC<FeedPageProps> = ({ user, appSyncUser }) => {
  const [composerText, setComposerText] = useState('');
  const [posts, setPosts] = useState<FeedPost[]>(mockFeedPosts);

  const userAvatar = appSyncUser?.picture_url || appSyncUser?.avatarUrl || user?.avatar;
  const userName = appSyncUser?.firstName || user?.name || 'You';

  const handleSubmit = useCallback(() => {
    if (!composerText.trim()) return;

    const newPost: FeedPost = {
      id: `local-${Date.now()}`,
      author: {
        id: appSyncUser?.id || 'current-user',
        name: userName,
        avatarUrl: userAvatar,
      },
      location: 'Your location',
      caption: composerText.trim(),
      likes: 0,
      comments: 0,
      createdAt: new Date().toISOString(),
    };

    setPosts((prev) => [newPost, ...prev]);
    setComposerText('');
  }, [composerText, appSyncUser?.id, userName, userAvatar]);

  const handleLike = useCallback((postId: string) => {
    setPosts((prev) =>
      prev.map((post) => {
        if (post.id !== postId) return post;
        const isLiked = !post.isLiked;
        return {
          ...post,
          isLiked,
          likes: isLiked ? post.likes + 1 : Math.max(0, post.likes - 1),
        };
      })
    );
  }, []);

  return (
    <DashboardLayout activeSection="feed">
      <div className="v2-feed-page">
        <header className="v2-feed-page__header">
          <h1 className="v2-feed-page__title">Good morning, {userName}</h1>
        </header>

        <section className="v2-feed-page__recommendations" aria-labelledby="recommendations-heading">
          <div className="v2-feed-page__section-header">
            <h2 id="recommendations-heading">Recommended for you</h2>
            <button type="button">See all</button>
          </div>
          <div className="v2-feed-page__recommendation-list">
            {recommendedTrips.map((trip) => (
              <article key={trip.title} className="v2-feed-page__recommendation-card">
                <img src={trip.imageUrl} alt="" />
                <div>
                  <h3>{trip.title}</h3>
                  <p>{trip.meta}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <ShareComposer
          userAvatar={userAvatar}
          userName={userName}
          value={composerText}
          onChange={setComposerText}
          onSubmit={handleSubmit}
        />

        <section className="v2-feed-page__feed" aria-labelledby="feed-heading">
          <h2 id="feed-heading" className="v2-feed-page__section-title">Travel Feed</h2>
          <FeedList
            posts={posts}
            onLike={handleLike}
            onComment={(id) => console.log('Comment on', id)}
            onShare={(id) => console.log('Share', id)}
          />
        </section>
      </div>
    </DashboardLayout>
  );
};

export default FeedPage;
